/**
 * The six benefit categories the employee self-selects across, in display order
 * (scope §Screens: Medical, Dental, Vision, Accident, Critical Illness, Hospital
 * Indemnity). The context API tags each offered plan with a `category`; the
 * per-plan screens (`PlanScreen`) are driven one category at a time in THIS
 * order. Any category the employer did not offer is simply skipped (no empty
 * screen).
 *
 * The `Major Medical` alias mirrors the backend `plan-category.enum` label used
 * in the §2.5 snapshot / §4 context wire (`"category": "Major Medical"`) so the
 * Medical screen catches it regardless of which spelling the wire carries.
 */
export const SELECTION_CATEGORY_ORDER = [
  'Medical',
  'Dental',
  'Vision',
  'Accident',
  'Critical Illness',
  'Hospital Indemnity',
] as const;

export type SelectionCategory = (typeof SELECTION_CATEGORY_ORDER)[number];

/** Wire category label → the display bucket it belongs to. */
export function normalizeCategory(raw: string): string {
  const trimmed = raw.trim();
  if (/^major\s*medical$/i.test(trimmed) || /^medical$/i.test(trimmed)) {
    return 'Medical';
  }
  // Case-insensitive match to a known bucket; otherwise keep the wire label so an
  // unexpected category still renders its own screen rather than vanishing.
  const known = SELECTION_CATEGORY_ORDER.find(
    (c) => c.toLowerCase() === trimmed.toLowerCase()
  );
  return known ?? trimmed;
}

/** Sort key for a (possibly unknown) category — knowns first, in order. */
export function categoryRank(category: string): number {
  const idx = SELECTION_CATEGORY_ORDER.indexOf(category as SelectionCategory);
  return idx === -1 ? SELECTION_CATEGORY_ORDER.length : idx;
}

/**
 * Who's-covered tier labels for the `insurance_prices_coverage_enum` codes,
 * matching the B2C `calculateCoverageLevel` mapping
 * (`src/features/onboarding/utils/coverageLevel.ts`): 1 Individual, 2 +Spouse,
 * 3 +Children, 4 Family. Unknown codes fall back to a generic label.
 */
export const COVERAGE_LABELS: Record<number, string> = {
  1: 'Employee only',
  2: 'Employee + Spouse',
  3: 'Employee + Child(ren)',
  4: 'Employee + Family',
};

export function coverageLabel(code: number): string {
  return COVERAGE_LABELS[code] ?? `Coverage ${code}`;
}
